import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { REGION_STYLE } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Save, Loader2 } from "lucide-react";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleReviewGrid({ onChanged, highlightedId }) {
  const [grid, setGrid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const serverSignatureRef = useRef("");

  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  const load = useCallback(async (notify = false) => {
    const res = await axios.get(`${API}/reports/google_review/grid`);
    const signature = JSON.stringify(res.data);
    const changed = Boolean(serverSignatureRef.current) && signature !== serverSignatureRef.current;
    if (dirtyRef.current) {
      if (changed) {
        toast.info(<span data-testid="google-review-sync-warning">New review data is available</span>, {
          id: "google-review-sync-pending",
          description: <span data-testid="google-review-sync-warning-description">Save your current edits before syncing.</span>,
        });
      }
      return;
    }
    serverSignatureRef.current = signature;
    setGrid(res.data);
    if (notify && changed) {
      toast.success(<span data-testid="google-review-sync-toast">New data synced</span>, {
        id: "google-review-auto-sync",
        description: <span data-testid="google-review-sync-toast-description">Changes from another device are now visible.</span>,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncGrid = useCallback(async () => {
    try { await load(true); }
    catch (e) { if (e?.response?.status !== 401) console.error("Google Review auto sync failed", e); }
  }, [load]);
  useVisibilityPolling(syncGrid, true, 5000);

  if (!grid) return <div className="py-24 text-center text-slate-400">Loading…</div>;

  const setVal = (region, outlet, month, value) => {
    dirtyRef.current = true;
    setDirty(true);
    setGrid((g) => ({
      ...g,
      regions: g.regions.map((rg) => rg.region !== region ? rg : {
        ...rg,
        outlets: rg.outlets.map((o) => o.outlet !== outlet ? o : { ...o, [month]: value }),
      }),
    }));
  };

  const rowTotal = (o) => grid.months.reduce((s, m) => s + (Number(o[m]) || 0), 0);
  const colTotal = (m) => grid.regions.reduce((s, rg) => s + rg.outlets.reduce((a, o) => a + (Number(o[m]) || 0), 0), 0);
  const grandTotal = grid.regions.reduce((s, rg) => s + rg.outlets.reduce((a, o) => a + rowTotal(o), 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const rows = [];
      grid.regions.forEach((rg) => rg.outlets.forEach((o) => {
        const months = {};
        grid.months.forEach((m) => (months[m] = o[m] === "" || o[m] == null ? "" : Number(o[m])));
        rows.push({ outlet: o.outlet, code: o.code, months });
      }));
      await axios.post(`${API}/reports/google_review/bulk`, rows);
      setDirty(false);
      dirtyRef.current = false;
      toast.success("Saved all branches");
      onChanged && onChanged();
      load();
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 shadow-sm border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-900">Monthly Review Grid</h3>
          <p className="text-xs text-slate-400">Type each branch's monthly review count directly — no need to add records one by one.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="grid-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save All
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="gr-grid">
          <thead>
            <tr className="border-b-2 border-slate-200">
              <th className="py-2 px-2 text-left text-[11px] font-bold uppercase text-slate-500">Code</th>
              <th className="py-2 px-2 text-left text-[11px] font-bold uppercase text-slate-500 min-w-[160px]">Outlet</th>
              {grid.month_labels.map((m) => <th key={m} className="py-2 px-1 text-center text-[11px] font-bold uppercase text-slate-500">{m}</th>)}
              <th className="py-2 px-2 text-center text-[11px] font-bold uppercase text-slate-500">TOL</th>
            </tr>
          </thead>
          <tbody>
            {grid.regions.map((rg) => (
              <Fragment key={rg.region}>
                <tr>
                  <td colSpan={grid.months.length + 3} className="py-1.5 px-3 font-bold text-white text-center"
                    style={{ backgroundColor: REGION_STYLE[rg.region].banner }}>{rg.region}</td>
                </tr>
                {rg.outlets.map((o) => (
                  <tr key={o.outlet} data-search-record-id={o.id || undefined}
                    data-testid={o.id ? `google-review-record-row-${o.id}` : undefined}
                    className={`border-b border-slate-100 transition-[box-shadow] duration-300 ${o.id && o.id === highlightedId ? "relative z-10 ring-4 ring-inset ring-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.22)]" : ""}`}
                    style={{ backgroundColor: REGION_STYLE[rg.region].tint }}>
                    <td className="py-1 px-2 font-semibold text-slate-600 text-xs">{o.code}</td>
                    <td className="py-1 px-2 font-medium text-slate-800">{o.outlet}</td>
                    {grid.months.map((m) => (
                      <td key={m} className="py-1 px-0.5">
                        <input type="number" value={o[m] ?? ""} data-testid={`grid-${o.outlet}-${m}`}
                          onChange={(e) => setVal(rg.region, o.outlet, m, e.target.value)}
                          className="w-11 text-center text-xs py-1 rounded border border-transparent bg-white/60 focus:border-amber-500 focus:bg-white outline-none font-mono-num" />
                      </td>
                    ))}
                    <td className="py-1 px-2 text-center font-bold text-slate-900 font-mono-num">{rowTotal(o) || ""}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
            <tr className="border-t-2 border-slate-900 bg-slate-50">
              <td colSpan={2} className="py-2 px-2 text-right font-extrabold text-blue-700">Total</td>
              {grid.months.map((m) => <td key={m} className="py-2 px-1 text-center font-extrabold text-blue-700 font-mono-num">{colTotal(m) || 0}</td>)}
              <td className="py-2 px-2 text-center font-extrabold text-blue-700 font-mono-num" data-testid="gr-grand-total">{grandTotal}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
